import { useMemo, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { formatNome } from '@/utils/format'
import { formatCPF } from '@/components/form/input/cpf-mask-input'
import { Badge } from '@/components/ui/badge'
import VerificationSignaturePage from './VerificationSignaturePage'
import { useFetchSignatureByIdTenant } from '../services/useFetchSignatureByIdTenant'
import { useCurrentUser } from '@/features/auth/state/auth'
import { useGetFilterType, useGetSearchTerm } from '../state/filters'

type SignatureRow = {
  cpf?: string | null
  id_owner?: string | null
  id_user?: string | null
  nome?: string | null
  isAtivo: boolean
  isModifica: boolean
  isVerifed: boolean
}

export default function ListSignatures() {
  const [selected, setSelected] = useState<SignatureRow | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const user = useCurrentUser()
  const filterType = useGetFilterType()
  const searchTerm = useGetSearchTerm()

  // O endpoint retorna um array simples de SignatureRow
  const { data: signatures = [] } = useFetchSignatureByIdTenant(user?.id_tenant)

  const handleRowClick = (row: SignatureRow) => {
    setSelected(row)
    setDialogOpen(true)
  }

  const renderRow = (row: SignatureRow) => {
    const nome = row.nome ? formatNome(row.nome) : '—'
    const identificador = row.cpf ? formatCPF(row.cpf) : '—'
    const statusLegivel = row.isVerifed ? 'Verificado' : 'Não verificado'
    const tipo = getSignatureType(row)
    const key = row.id_user ?? row.id_owner ?? row.cpf ?? crypto.randomUUID()

    return (
      <TableRow key={key} onClick={() => handleRowClick(row)} className="cursor-pointer hover:bg-muted">
        <TableCell className="w-[56%] text-left">
          <span className="block text-sm text-muted-foreground">{nome}</span>
          <span className="block font-medium">{identificador}</span>
        </TableCell>
        <TableCell className="w-[20%] text-center">
          <Badge variant="outline">{tipo}</Badge>
        </TableCell>
        <TableCell className="w-[24%] text-center">{statusLegivel}</TableCell>
      </TableRow>
    )
  }

  const filteredBase = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase()
    const normalizedDigits = searchTerm.replace(/\D/g, '')

    return (signatures as SignatureRow[])
      .filter(signature => signature.isModifica === false)
      .filter(signature => {
        if (filterType === 'clientes') return Boolean(signature.id_owner)
        if (filterType === 'usuarios') return Boolean(signature.id_user) && !signature.id_owner
        return true
      })
      .filter(signature => {
        if (!normalizedSearch) return true

        const nome = String(signature.nome ?? '').toLowerCase()
        const cpf = String(signature.cpf ?? '')
        const cpfDigits = cpf.replace(/\D/g, '')
        const type = getSignatureType(signature).toLowerCase()

        return (
          nome.includes(normalizedSearch) ||
          cpf.toLowerCase().includes(normalizedSearch) ||
          type.includes(normalizedSearch) ||
          (!!normalizedDigits && cpfDigits.includes(normalizedDigits))
        )
      })
  }, [filterType, searchTerm, signatures])

  const filterSignatures = (isVerifed: boolean) => filteredBase.filter(signature => signature.isVerifed === isVerifed)

  // id e role para a página de verificação
  const selectedId = selected?.id_user ?? selected?.id_owner ?? ''
  const selectedRole: 'user' | 'owner' | null = selected?.id_user ? 'user' : selected?.id_owner ? 'owner' : null

  return (
    <>
      <Tabs defaultValue="nao-verificado">
        <TabsList className="mb-4">
          <TabsTrigger value="nao-verificado">Não Verificados</TabsTrigger>
          <TabsTrigger value="verificado">Verificados</TabsTrigger>
        </TabsList>

        <TabsContent value="nao-verificado">
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">Assinaturas Não Verificadas</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[56%] text-left font-bold">Nome / Doc</TableHead>
                    <TableHead className="w-[20%] text-center font-bold">Tipo</TableHead>
                    <TableHead className="w-[24%] text-center font-bold">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>{filterSignatures(false).map(renderRow)}</TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="verificado">
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">Assinaturas Verificadas</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[56%] text-left font-bold">Nome / Doc</TableHead>
                    <TableHead className="w-[20%] text-center font-bold">Tipo</TableHead>
                    <TableHead className="w-[24%] text-center font-bold">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>{filterSignatures(true).map(renderRow)}</TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Verificação da Assinatura</DialogTitle>
          </DialogHeader>

          {selected && selectedId && selectedRole && (
            <VerificationSignaturePage key={`${selectedRole}-${selectedId}`} id={selectedId} role={selectedRole} />
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

function getSignatureType(row: SignatureRow) {
  return row.id_owner ? 'Cliente' : 'Parceiro'
}
